import Lenis from "@studio-freight/lenis";

/**
 * Lenisの初期設定を行います。
 * @return {Lenis} Lenisのインスタンス
 */
export const setupLenis = () => {
  const lenis = new Lenis({
    lerp: .12, // 慣性の強さ
    duration: 1.2, // スクロールアニメーションの時間
    easing: easeOutQuart, // イージング関数
    smoothTouch: true, // タッチイベントでの挙動（有効）
  });

  // html { height: 100% } のため document.documentElement 自体の「箱としての
  // サイズ」は常にビューポート高に固定されており、Lenisの autoResize
  // （ResizeObserverでdocument.documentElementを監視）だけでは
  // 画面外で読み込まれる遅延画像等によるページ実高の増加を検知できない。
  //
  // さらに、ホイールを一度に大きく回した場合、Lenisはその瞬間の(まだ古い)
  // limitを基準にスクロール先(targetScroll)を確定してしまう。後からlimitが
  // 伸びても、Lenisは「目的地に到達した」ものとして止まったままになり、
  // 追加の操作をしない限り再度動き出さない。
  //
  // → resize()を呼ぶ箇所(画像のloadイベント／定期チェック)すべてで、
  //   必ずこの safeResize() 経由にする。「resize直前にlimit付近まで
  //   到達していた」かつ「resizeでlimitが伸びた」場合は、伸びた分だけ
  //   自動でscrollToを追いかけさせる。呼び出し元を一本化することで、
  //   複数の画像がほぼ同時に読み込まれても取りこぼさないようにする。
  const NEAR_LIMIT_THRESHOLD = 4; // px

  lenis.safeResize = () => {
    const prevLimit = lenis.limit;
    const wasNearLimit = lenis.targetScroll >= prevLimit - NEAR_LIMIT_THRESHOLD;

    lenis.resize();

    if (wasNearLimit && lenis.limit > prevLimit) {
      // programmatic(既定値 true)だと、アニメーション中ずっと
      // targetScroll が「現在のアニメーション位置」で上書きされ続けてしまい、
      // 次のsafeResize()時に「目的地(=limit)に向かっている最中」だと
      // 判定できなくなる（実際のホイール操作と同じ programmatic:false で
      // 呼ぶことで、targetScroll が最終目的地のまま保持されるようにする）。
      lenis.scrollTo(lenis.limit, {
        lerp: lenis.options.lerp,
        duration: lenis.options.duration,
        easing: lenis.options.easing,
        programmatic: false,
      });
    }
  };

  let lastResizeAt = 0;
  const RESIZE_INTERVAL = 400; // ms

  function raf(time) {
    if (time - lastResizeAt > RESIZE_INTERVAL) {
      lastResizeAt = time;
      lenis.safeResize();
    }
    lenis.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  return lenis;
};

/**
 * イージング関数
 * @see https://easings.net/ja
 * @param {number} x アニメーションの進行度（正規化された0から1までの値）
 * @return {number} イージングを適用した後の進行度（正規化された0から1までの値）
 */
const easeOutQuart = (x) => {
  return 1 - Math.pow(1 - x, 4);
};
